import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Aside,
  TopBar,
  BrandText,
  Nav,
  SectionTitle,
  Item,
  Label,
  Bottom,
  Divider,
  Hint,
  LogoImg,
  ProfileBox,
  ProfileIcon,
  Nickname,
  AdminBadge,
} from "./styles";
import {
  CREATE_ICON,
  DASHBOARD_ICON,
  FOLDER_ICON,
  HYPERVISOR_ICON,
  INSTANCE_ICON,
  LOGOUT_ICON,
  METRIC_ICON,
  STORAGE_ICON,
  USER_ICON,
  WORKING_ICON,
} from "./path";
import Icon from "./icon";
import { useConfirmStore } from "modal/confirm/store";
import { userAPI } from "api";

const MENU_SECTIONS = [
  {
    title: "MONITOR CENTER",
    items: [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: DASHBOARD_ICON,
        permissions: { domainAdmin: true, ouAdmin: true, user: true },
      },
      {
        label: "Hypervisor",
        path: "/hypervisor",
        icon: HYPERVISOR_ICON,
        permissions: { domainAdmin: true },
      },
      {
        label: "Instance",
        path: "/instance",
        icon: INSTANCE_ICON,
        permissions: { domainAdmin: true, ouAdmin: true },
      },
    ],
  },
  {
    title: "MANAGER",
    items: [
      {
        label: "Metric",
        path: "/metric",
        icon: METRIC_ICON,
        permissions: { domainAdmin: true, ouAdmin: true, user: true },
      },
      {
        label: "Working",
        path: "/working",
        icon: WORKING_ICON,
        permissions: { domainAdmin: true, ouAdmin: true },
      },
    ],
  },
  {
    title: "ADMIN",
    items: [
      {
        label: "Create",
        path: "/create",
        icon: CREATE_ICON,
        permissions: { domainAdmin: true },
      },
    ],
  },
  {
    title: "MAINTENANCE",
    items: [
      {
        label: "User",
        path: "/user",
        icon: USER_ICON,
        permissions: { domainAdmin: true, ouAdmin: true },
      },
      {
        label: "Ftp",
        path: "/ftp",
        icon: FOLDER_ICON,
        permissions: { domainAdmin: true, ouAdmin: true },
      },
      {
        label: "Storage",
        path: "/storage",
        icon: STORAGE_ICON,
        permissions: { domainAdmin: true, ouAdmin: true },
      },
    ],
  },
];

const SideBar = () => {
  const { openConfirm } = useConfirmStore();
  const [department, setDepartment] = useState();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const username = sessionStorage.getItem("username") ?? "Guest";
  const ouAdmin = sessionStorage.getItem("ou_admin") === "true";
  const domainAdmin = sessionStorage.getItem("domain_admin") === "true";

  const role =
    ouAdmin && domainAdmin ? "A" : ouAdmin ? "O" : domainAdmin ? "D" : "U";

  const [collapsed, setCollapsed] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const fetchUser = async () => {
      if (username === "Guest" || !sessionStorage.getItem("access_token"))
        return;

      try {
        const data = await userAPI.getUser(username);
        setDepartment(data?.data.data.department);
      } catch (err) {
        console.error("사용자 정보를 가져오지 못했습니다:", err);
      }
    };

    fetchUser();

    const handleResize = () => setCollapsed(window.innerWidth < 1200);
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [username]);

  // ✅ 권한 + 부서 기반 메뉴 표시 로직
  const hasPermission = (permissions, label) => {
    if (domainAdmin && permissions.domainAdmin) return true;
    if (ouAdmin && permissions.ouAdmin) return true;
    if (!ouAdmin && !domainAdmin && permissions.user) return true;

    // ✅ PRODUCTION 또는 PIPELINE 부서는 Storage 메뉴 허용
    if (
      label === "Storage" &&
      (department === "PRODUCTION" || department === "PIPELINE")
    ) {
      return true;
    }

    return false;
  };

  const handleLogout = () => {
    openConfirm({
      title: "로그아웃 하시겠습니까?",
      desc: "확인 버튼을 누르면 로그아웃을 진행합니다.",
      onConfirm: async () => {
        sessionStorage.clear();
        navigate("/login");
      },
    });
  };

  const handleNav = (path: string) => navigate(path);
  const isActive = (path: string) =>
    pathname === path || pathname.startsWith(path + "/");

  return (
    <Aside $collapsed={collapsed}>
      <TopBar $collapsed={collapsed} onClick={() => handleNav("/dashboard")}>
        <LogoImg src="/logo.svg" alt="CMP logo" $collapsed={collapsed} />
        {!collapsed && <BrandText>CMP</BrandText>}
      </TopBar>

      <Divider />

      <ProfileBox $collapsed={collapsed} $isAdmin={ouAdmin || domainAdmin}>
        <ProfileIcon viewBox="0 0 24 24" aria-hidden>
          <path
            d="M12 11c2.8 0 5-2.2 5-5s-2.2-5-5-5-5 2.2-5 5 2.2 5 5 5z M4 20c0-4 4-6 8-6s8 2 8 6"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
        </ProfileIcon>
        {!collapsed && (
          <>
            <Nickname>{username}</Nickname>
            <AdminBadge $role={role}>{role}</AdminBadge>
          </>
        )}
      </ProfileBox>

      <Divider />

      <Nav>
        {MENU_SECTIONS.map((section, i) => {
          const visibleItems = section.items.filter((item) =>
            hasPermission(item.permissions, item.label)
          );
          if (visibleItems.length === 0) return null;

          return (
            <React.Fragment key={section.title ?? `sec-${i}`}>
              {section.title && (
                <SectionTitle $collapsed={collapsed}>
                  {section.title}
                </SectionTitle>
              )}
              {visibleItems.map((item) => (
                <Item
                  key={item.path}
                  $active={isActive(item.path)}
                  $collapsed={collapsed}
                  onClick={() => handleNav(item.path)}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon path={item.icon} />
                  {!collapsed && <Label>{item.label}</Label>}
                </Item>
              ))}
              {i < MENU_SECTIONS.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </Nav>

      <Bottom>
        <Divider />
        <Item $collapsed={collapsed} onClick={handleLogout} $danger>
          <Icon path={LOGOUT_ICON} />
          {!collapsed && <Label>Logout</Label>}
        </Item>
        <Hint $collapsed={collapsed}>{collapsed ? " " : "© OVP Center"}</Hint>
      </Bottom>
    </Aside>
  );
};

export default SideBar;
